/**
 * Wraps `React.lazy(() => import(...))` so that if the dynamic import
 * throws a `ChunkLoadError` (typically because the user has an open
 * tab from before a deploy and webpack's hashed chunk no longer exists),
 * we retry once after forcing a hard reload.
 *
 * Without this, the user sees a blank or broken UI and has to manually
 * refresh. With it, the page silently reloads and serves the new bundle.
 */
import React from 'react';

const RELOAD_KEY = 'travelease.chunk-reload-attempt';

export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const result = await factory();
      // Successful load → clear the retry flag so future genuine errors
      // can still trigger a single recovery attempt.
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem(RELOAD_KEY);
        }
      } catch {
        /* ignore storage errors */
      }
      return result;
    } catch (err: any) {
      const isChunkError =
        err?.name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(String(err?.message || '')) ||
        /Loading CSS chunk/i.test(String(err?.message || ''));

      if (!isChunkError) {
        throw err;
      }

      // Only attempt a hard reload once per session to avoid a reload loop.
      let alreadyTried = false;
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          alreadyTried = window.sessionStorage.getItem(RELOAD_KEY) === '1';
          window.sessionStorage.setItem(RELOAD_KEY, '1');
        }
      } catch {
        /* ignore storage errors */
      }

      if (!alreadyTried && typeof window !== 'undefined') {
        // Hard-reload the page so the browser fetches the new index.html
        // and the new chunk hashes.
        window.location.reload();
        // Return a never-resolving promise so React doesn't try to render
        // the broken lazy component before the reload completes.
        return await new Promise<{ default: T }>(() => {});
      }

      throw err;
    }
  });
}
